import { useCallback, useEffect, useState } from "react";
import { addPatientNote, deletePatientNote, getPatientNotes, updatePatientNote } from "../../../utils/apis/patient-notes-service";
import { useAuth } from "../../../utils/context/AuthContext";

export interface PatientNote {
    id: string;
    userId: string;
    expertId: string;
    note: string;
    created_at: string;
    updated_at: string;
}

export const usePatientNotes = (patientId: string | null, userId: string | null) => {
    const { auth } = useAuth();
    const [notes, setNotes] = useState<PatientNote[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Load notes from API
    const loadNotes = useCallback(async () => {
        if (!patientId || !userId || !auth.idToken) {
            setNotes([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const patientNotes = await getPatientNotes(patientId, userId, auth.idToken);
            // Filter notes by both patientId and userId (expert isolation)
            setNotes(patientNotes.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()));
        } catch (err: any) {
            console.error("Failed to load notes:", err);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    }, [patientId, userId, auth.idToken]);

    // Add a new note
    const addNote = useCallback(async (text: string, userId: string) => {
        if (!patientId || !text.trim() || !auth.idToken) return;

        try {
            await addPatientNote({
                patientId,
                expertId: userId,
                text: text.trim(),
            }, auth.idToken);

            // Reload all notes from server to ensure consistency
            await loadNotes();
        } catch (err: any) {
            console.error("Failed to add note:", err);
            setError(err);
        }
    }, [patientId, auth.idToken, loadNotes]);

    // Update an existing note
    const updateNote = useCallback(async (noteId: string, text: string) => {
        if (!text.trim() || !auth.idToken) return;

        try {
            const updatedNote = await updatePatientNote(noteId, text.trim(), auth.idToken);
            
            setNotes(prev => 
                prev.map(note => note.id === noteId ? updatedNote : note)
                .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
            );
        } catch (err: any) {
            console.error("Failed to update note:", err);
            setError(err);
        }
    }, [auth.idToken]);

    // Delete a note
    const deleteNote = useCallback(async (noteId: string) => {
        if (!auth.idToken) return;

        try {
            await deletePatientNote(noteId, auth.idToken);
            setNotes(prev => prev.filter(note => note.id !== noteId));
        } catch (err: any) {
            console.error("Failed to delete note:", err);
            setError(err);
        }
    }, [auth.idToken]);

    // Load notes when patientId, userId, or token changes
    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    return {
        notes,
        isLoading,
        error,
        addNote,
        updateNote,
        deleteNote,
        refetch: loadNotes,
    };
}; 